import React, { useState } from "react";
import { saveAuth } from "../auth";
import API_BASE_URL from "../services/config";
import { normalizeAuthUser } from "../utils/normalizeAuthUser";

const GOOGLE_OAUTH_URL =
  process.env.REACT_APP_GOOGLE_OAUTH_URL ||
  `${API_BASE_URL}/api/auth/google`;
const FORGOT_PASSWORD_ENDPOINT = `${API_BASE_URL}/api/auth/forgot-password`;

const getForgotPasswordFeedback = (response, data) => {
  if (response.ok) {
    return {
      type: "success",
      message:
        data.message ||
        "If an account exists for this email, a reset link will be sent.",
    };
  }

  if (response.status === 400) {
    return {
      type: "error",
      message:
        data.message || "Please enter a valid email address and try again.",
    };
  }

  if (response.status === 401) {
    return {
      type: "error",
      message:
        data.message ||
        "Password reset is temporarily unavailable. Please refresh and try again.",
    };
  }

  if (response.status === 500) {
    return {
      type: "error",
      message:
        data.message ||
        "We could not send a reset email right now. Please try again in a few minutes.",
    };
  }

  return {
    type: "error",
    message:
      data.message ||
      `Password reset request failed (${response.status}). Please try again.`,
  };
};

const Login = ({ onClose, onLogin }) => {
  const [mode, setMode] = useState("login");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  const isLogin = mode === "login";
  const isForgotPassword = mode === "forgot";
  const isMobile = window.innerWidth <= 768;
  const isSmallPhone = window.innerWidth <= 480;

  const clearMessages = () => {
    setError("");
    setStatusMessage("");
  };

  const handleChange = (e) => {
    setFormData((previous) => ({
      ...previous,
      [e.target.name]: e.target.value,
    }));
    clearMessages();
  };

  const switchMode = (nextMode) => {
    if (isLoading) {
      return;
    }

    clearMessages();
    setMode(nextMode);
    setFormData((previous) => ({
      name: nextMode === "signup" ? previous.name : "",
      email: previous.email,
      password: "",
      confirmPassword: "",
    }));
  };

  const handleGoogleSignIn = () => {
    clearMessages();

    if (GOOGLE_OAUTH_URL) {
      window.location.href = GOOGLE_OAUTH_URL;
      return;
    }

      setStatusMessage(
        "Google sign-in could not start. Please make sure the backend Google OAuth endpoint is publicly accessible."
      );
  };

  const handleForgotPasswordSubmit = async () => {
    if (!formData.email.trim()) {
      throw new Error("Please enter your email address first.");
    }

    try {
      const response = await fetch(FORGOT_PASSWORD_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));
      const feedback = getForgotPasswordFeedback(response, data);

      if (feedback.type === "success") {
        setStatusMessage(feedback.message);
        return;
      }

      setError(feedback.message);
    } catch (forgotPasswordError) {
      console.error("Forgot password request failed:", forgotPasswordError);
      setError(
        `We could not reach the server at ${API_BASE_URL}. Please make sure the backend is running and try again.`
      );
    }
  };

  const handleAuthSubmit = async () => {
    if (mode === "signup") {
      if (formData.password !== formData.confirmPassword) {
        throw new Error("Passwords do not match.");
      }

      if (formData.password.length < 6) {
        throw new Error("Password must be at least 6 characters long.");
      }

      if (!formData.name.trim()) {
        throw new Error("Full name is required.");
      }

      if (!formData.email.includes("@")) {
        throw new Error("Please enter a valid email address.");
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Signup failed. Please try again.");
      }

      const userData = normalizeAuthUser(data);

      if (userData) {
        saveAuth(userData);
        onLogin?.(userData);
        return;
      }

      setStatusMessage("Account created successfully. Please sign in.");
      setMode("login");
      setFormData((previous) => ({
        ...previous,
        name: "",
        password: "",
        confirmPassword: "",
      }));
      return;
    }

    if (!formData.email || !formData.password) {
      throw new Error("Please enter both email and password.");
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: formData.email.trim(),
        password: formData.password,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || data.error || `Login failed (${response.status})`
      );
    }

    const userData = normalizeAuthUser(data);

    if (!userData) {
      throw new Error(
        "Login succeeded but the frontend could not process the user payload."
      );
    }

    saveAuth(userData);
    onLogin?.(userData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setIsLoading(true);

    try {
      if (isForgotPassword) {
        await handleForgotPasswordSubmit();
      } else {
        await handleAuthSubmit();
      }
    } catch (submitError) {
      console.error("Auth flow error:", submitError);
      setError(submitError.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.85)",
      backdropFilter: "blur(10px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      padding: isSmallPhone ? "10px" : isMobile ? "15px" : "20px",
    },
    modal: {
      background:
        "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)",
      borderRadius: isSmallPhone ? "16px" : isMobile ? "20px" : "24px",
      padding: isSmallPhone ? "20px" : isMobile ? "25px" : "30px",
      width: "100%",
      maxWidth: isSmallPhone ? "90vw" : isMobile ? "85vw" : "420px",
      minWidth: isSmallPhone ? "auto" : "300px",
      border: "2px solid rgba(97, 218, 251, 0.4)",
      boxShadow: "0 20px 40px rgba(97, 218, 251, 0.25)",
      position: "relative",
      overflow: "hidden",
      maxHeight: "90vh",
      overflowY: "auto",
    },
    backgroundPattern: {
      position: "absolute",
      inset: 0,
      background: `
        radial-gradient(circle at 10% 10%, rgba(97, 218, 251, 0.08) 0%, transparent 40%),
        radial-gradient(circle at 90% 10%, rgba(33, 161, 241, 0.06) 0%, transparent 40%),
        radial-gradient(circle at 50% 90%, rgba(97, 218, 251, 0.05) 0%, transparent 40%)
      `,
      zIndex: 0,
    },
    closeButton: {
      position: "absolute",
      top: isSmallPhone ? "12px" : "15px",
      right: isSmallPhone ? "12px" : "15px",
      background: "rgba(97, 218, 251, 0.15)",
      border: "1px solid rgba(97, 218, 251, 0.4)",
      borderRadius: "50%",
      width: isSmallPhone ? "30px" : "35px",
      height: isSmallPhone ? "30px" : "35px",
      color: "#61dafb",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: isSmallPhone ? "18px" : "20px",
      zIndex: 2,
      transition: "all 0.3s ease",
    },
    content: {
      position: "relative",
      zIndex: 1,
    },
    header: {
      textAlign: "center",
      marginBottom: isSmallPhone ? "18px" : isMobile ? "22px" : "26px",
    },
    logo: {
      fontSize: isSmallPhone ? "2rem" : isMobile ? "2.3rem" : "2.6rem",
      marginBottom: "14px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: isSmallPhone ? "60px" : "68px",
      height: isSmallPhone ? "60px" : "68px",
      borderRadius: "18px",
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      color: "#08111c",
      boxShadow: "0 0 24px rgba(97, 218, 251, 0.35)",
    },
    title: {
      fontSize: isSmallPhone ? "1.45rem" : isMobile ? "1.6rem" : "1.8rem",
      fontWeight: "800",
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      marginBottom: "8px",
      lineHeight: 1.2,
    },
    subtitle: {
      color: "#94bbe9",
      fontSize: isSmallPhone ? "0.85rem" : isMobile ? "0.9rem" : "1rem",
      opacity: 0.9,
      lineHeight: 1.5,
    },
    messageBox: {
      fontSize: isSmallPhone ? "0.78rem" : "0.85rem",
      marginBottom: "14px",
      padding: "10px 12px",
      borderRadius: "10px",
      lineHeight: 1.45,
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: isSmallPhone ? "12px" : isMobile ? "14px" : "16px",
    },
    socialButton: {
      background: "rgba(255, 255, 255, 0.06)",
      color: "#ffffff",
      border: "1px solid rgba(255, 255, 255, 0.16)",
      borderRadius: "12px",
      padding: isSmallPhone ? "12px 14px" : "14px 16px",
      fontSize: isSmallPhone ? "0.9rem" : "0.98rem",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
    },
    googleMark: {
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background:
        "conic-gradient(from 180deg, #34a853 0deg 90deg, #4285f4 90deg 180deg, #ea4335 180deg 270deg, #fbbc05 270deg 360deg)",
      color: "#0a0a0a",
      fontSize: "0.8rem",
      fontWeight: "900",
      flexShrink: 0,
    },
    dividerRow: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      margin: "6px 0 2px 0",
    },
    dividerLine: {
      flex: 1,
      height: "1px",
      background: "rgba(97, 218, 251, 0.15)",
    },
    dividerText: {
      color: "#7ea7d6",
      fontSize: "0.78rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      color: "#61dafb",
      fontSize: isSmallPhone ? "0.8rem" : "0.9rem",
      fontWeight: "600",
    },
    input: {
      background: "rgba(20, 20, 20, 0.9)",
      border: "1.5px solid rgba(97, 218, 251, 0.3)",
      borderRadius: "10px",
      padding: isSmallPhone ? "12px 14px" : isMobile ? "14px 16px" : "14px 18px",
      fontSize: isSmallPhone ? "0.9rem" : "1rem",
      color: "#ffffff",
      outline: "none",
      transition: "all 0.3s ease",
      width: "100%",
      boxSizing: "border-box",
    },
    helperText: {
      color: "#8ea8c8",
      fontSize: "0.78rem",
      lineHeight: 1.45,
      marginTop: "-2px",
    },
    linkRow: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: "-4px",
    },
    textButton: {
      background: "transparent",
      border: "none",
      color: "#61dafb",
      cursor: "pointer",
      fontSize: "0.84rem",
      fontWeight: "600",
      padding: 0,
    },
    submitButton: {
      background: "linear-gradient(135deg, #61dafb 0%, #21a1f1 100%)",
      color: "#050c14",
      border: "none",
      borderRadius: "12px",
      padding: isSmallPhone ? "14px" : isMobile ? "15px" : "16px",
      fontSize: isSmallPhone ? "0.95rem" : isMobile ? "1rem" : "1.05rem",
      fontWeight: "800",
      cursor: "pointer",
      transition: "all 0.3s ease",
      marginTop: "6px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    secondaryButton: {
      background: "rgba(97, 218, 251, 0.08)",
      color: "#b8dbff",
      border: "1px solid rgba(97, 218, 251, 0.2)",
      borderRadius: "12px",
      padding: isSmallPhone ? "12px" : "13px",
      fontSize: isSmallPhone ? "0.9rem" : "0.96rem",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    toggleText: {
      textAlign: "center",
      color: "#a0aec0",
      marginTop: "20px",
      fontSize: isSmallPhone ? "0.8rem" : isMobile ? "0.85rem" : "0.9rem",
      paddingTop: "15px",
      borderTop: "1px solid rgba(97, 218, 251, 0.1)",
    },
    toggleLink: {
      color: "#61dafb",
      cursor: "pointer",
      fontWeight: "700",
      textDecoration: "none",
      marginLeft: "4px",
    },
    footer: {
      textAlign: "center",
      marginTop: "15px",
      color: "#94bbe9",
      fontSize: isSmallPhone ? "0.7rem" : isMobile ? "0.75rem" : "0.8rem",
      lineHeight: 1.45,
    },
    loadingSpinner: {
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255, 255, 255, 0.35)",
      borderTop: "2px solid #ffffff",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
  };

  const spinnerStyle = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  const messageStyle = error
    ? {
        ...styles.messageBox,
        color: "#ffb4b4",
        background: "rgba(255, 107, 107, 0.12)",
        border: "1px solid rgba(255, 107, 107, 0.25)",
      }
    : {
        ...styles.messageBox,
        color: "#bde9ff",
        background: "rgba(97, 218, 251, 0.1)",
        border: "1px solid rgba(97, 218, 251, 0.2)",
      };

  const titleText = isForgotPassword
    ? "Reset Password"
    : isLogin
      ? "Welcome Back"
      : "Create Account";

  const subtitleText = isForgotPassword
    ? "Enter your email and we will send a password reset link if an account exists."
    : isLogin
      ? "Sign in to access divine wisdom"
      : "Join the DharmaAnswers journey";

  return (
    <>
      <style>{spinnerStyle}</style>
      <div style={styles.overlay} onClick={onClose}>
        <div
          style={styles.modal}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow =
              "0 25px 50px rgba(97, 218, 251, 0.3)";
            e.currentTarget.style.borderColor =
              "rgba(97, 218, 251, 0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow =
              "0 20px 40px rgba(97, 218, 251, 0.25)";
            e.currentTarget.style.borderColor =
              "rgba(97, 218, 251, 0.4)";
          }}
        >
          <div style={styles.backgroundPattern}></div>

          <button
            type="button"
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                "rgba(97, 218, 251, 0.3)";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                "rgba(97, 218, 251, 0.15)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {"\u00D7"}
          </button>

          <div style={styles.content}>
            <header style={styles.header}>
              <div style={styles.logo}>{"\u0950"}</div>
              <h1 style={styles.title}>{titleText}</h1>
              <p style={styles.subtitle}>{subtitleText}</p>
            </header>

            {(error || statusMessage) && (
              <div style={messageStyle}>{error || statusMessage}</div>
            )}

            <form style={styles.form} onSubmit={handleSubmit}>
              {!isForgotPassword && (
                <>
                  <button
                    type="button"
                    style={styles.socialButton}
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.borderColor =
                          "rgba(255, 255, 255, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor =
                        "rgba(255, 255, 255, 0.16)";
                    }}
                  >
                    <span style={styles.googleMark}>G</span>
                    Continue with Google
                  </button>

                  <div style={styles.dividerRow}>
                    <div style={styles.dividerLine}></div>
                    <span style={styles.dividerText}>or use email</span>
                    <div style={styles.dividerLine}></div>
                  </div>
                </>
              )}

              {!isLogin && !isForgotPassword && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    style={styles.input}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#61dafb";
                      e.target.style.boxShadow =
                        "0 0 15px rgba(97, 218, 251, 0.3)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor =
                        "rgba(97, 218, 251, 0.3)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              )}

              <div style={styles.inputGroup}>
                <label style={styles.label}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  style={styles.input}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#61dafb";
                    e.target.style.boxShadow =
                      "0 0 15px rgba(97, 218, 251, 0.3)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor =
                      "rgba(97, 218, 251, 0.3)";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {!isForgotPassword && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Password</label>
                  <input
                    type="password"
                    name="password"
                    placeholder={
                      isLogin
                        ? "Enter your password"
                        : "Create a password (minimum 6 characters)"
                    }
                    style={styles.input}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#61dafb";
                      e.target.style.boxShadow =
                        "0 0 15px rgba(97, 218, 251, 0.3)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor =
                        "rgba(97, 218, 251, 0.3)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              )}

              {isLogin && (
                <div style={styles.linkRow}>
                  <button
                    type="button"
                    style={styles.textButton}
                    onClick={() => switchMode("forgot")}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {!isLogin && !isForgotPassword && (
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    style={styles.input}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#61dafb";
                      e.target.style.boxShadow =
                        "0 0 15px rgba(97, 218, 251, 0.3)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor =
                        "rgba(97, 218, 251, 0.3)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>
              )}

              {isForgotPassword && (
                <p style={styles.helperText}>
                  If the email matches an account, you will receive a reset link
                  shortly.
                </p>
              )}

              <button
                type="submit"
                style={styles.submitButton}
                disabled={isLoading}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 20px rgba(97, 218, 251, 0.4)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <div style={styles.loadingSpinner}></div>
                    {isForgotPassword
                      ? "Preparing Reset..."
                      : isLogin
                        ? "Signing In..."
                        : "Creating Account..."}
                  </>
                ) : isForgotPassword ? (
                  "Send Reset Link"
                ) : isLogin ? (
                  "Sign In"
                ) : (
                  "Create Account"
                )}
              </button>

              {isForgotPassword && (
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => switchMode("login")}
                  disabled={isLoading}
                >
                  Back to Sign In
                </button>
              )}
            </form>

            {!isForgotPassword && (
              <div style={styles.toggleText}>
                {isLogin
                  ? "Don't have an account?"
                  : "Already have an account?"}
                <span
                  style={styles.toggleLink}
                  onClick={() => switchMode(isLogin ? "signup" : "login")}
                >
                  {isLogin ? "Sign Up" : "Sign In"}
                </span>
              </div>
            )}

            <footer style={styles.footer}>
              <p>
                By continuing, you agree to our Terms of Service and Privacy
                Policy.
              </p>
              <p style={{ marginTop: "5px", opacity: 0.75 }}>
                Ancient wisdom, modern access.
              </p>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
